import { createContext, ReactNode, useContext } from "react"
import { OrganizationContext } from "@/contexts/organization"
import { useSession } from "next-auth/react"

import {
  FeatureFlags,
  organizationFeatureFlags,
  subscriptionPlans,
} from "@/config/site"

/**
 * Define the keys for feature flags.
 */
type FeatureFlagKey = keyof FeatureFlags

/**
 * Interface for the Feature Flags context.
 */
interface FeatureFlagsContextType {
  /** Check if a given feature is enabled. */
  isFeatureEnabled: (featureFlag: FeatureFlagKey) => boolean
  /** Check if a given plan is enabled. */
  isPlanEnabled: (plan: keyof typeof subscriptionPlans) => boolean
}

/**
 * Create a context for Feature Flags.
 */
const FeatureFlagsContext = createContext<FeatureFlagsContextType>({
  isFeatureEnabled: () => false,
  isPlanEnabled: () => false,
})

/**
 * Feature Flags provider component.
 * @param children - React children elements.
 */
export const FeatureFlagsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { selectedOrganization } = useContext(OrganizationContext)
  const { data: session } = useSession()

  const subscriptionPlanKey =
    selectedOrganization?.subscription_plan ||
    session?.user?.UserOrganizationRole[0].Organization.subscription_plan
  const subscriptionPlan =
    subscriptionPlans[subscriptionPlanKey as keyof typeof subscriptionPlans]

  const isFeatureEnabled = (featureFlag: FeatureFlagKey): boolean => {
    // First, check if the feature is enabled by subscription plan
    const isFeatureEnabledByPlan =
      subscriptionPlan?.featureFlags[featureFlag] || false

    // Then, check if the feature is enabled for the organization
    if (!selectedOrganization?.id) {
      return false
    }
    const organizationId = selectedOrganization?.id
    const isFeatureEnabledByOrg =
      organizationFeatureFlags[organizationId]?.[featureFlag] || false

    // You can then decide how to combine these two booleans.
    // Here, I'm using logical OR as an example.
    return isFeatureEnabledByPlan || isFeatureEnabledByOrg
  }

  const isPlanEnabled = (plan: keyof typeof subscriptionPlans): boolean => {
    return subscriptionPlanKey === plan
  }

  return (
    <FeatureFlagsContext.Provider value={{ isFeatureEnabled, isPlanEnabled }}>
      {children}
    </FeatureFlagsContext.Provider>
  )
}

/**
 * Hook to use the Feature Flags context.
 * @returns The Feature Flags context.
 */
export const useFeatureFlags = () => useContext(FeatureFlagsContext)
